import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = anonymous
  const [token, setToken] = useState(() => localStorage.getItem("sniper_token"));

  const setSession = useCallback((tok, u) => {
    if (tok) {
      localStorage.setItem("sniper_token", tok);
      api.defaults.headers.common["Authorization"] = `Bearer ${tok}`;
    } else {
      localStorage.removeItem("sniper_token");
      delete api.defaults.headers.common["Authorization"];
    }
    setToken(tok || null);
    setUser(u ?? null);
  }, []);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      api.get("/auth/me")
        .then((r) => setUser(r.data))
        .catch(() => setSession(null, null));
    } else {
      setUser(null);
    }
  }, [token, setSession]);

  useEffect(() => {
    const id = api.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401) {
          setSession(null, null);
        }
        return Promise.reject(err);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, [setSession]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setSession(data.access_token, data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* silent */ }
    setSession(null, null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
